// src/components/LoanDatas/LoanNominees.tsx
import React, { useEffect, useState } from "react";
import Modal from "../Modal";
import { Icon } from "@iconify/react";
import Button from "../Button";
import FormBuilder, { FieldConfig } from "../FormBuilder";
import toast from "react-hot-toast";
import {
  getloanNominees,
  getloanNominee,
  createNominee,
  updateNominee,
  deleteNominee,
} from "../../services/loanService";
import { useAuth } from "../../context/AuthContext";

type Nominee = {
  nominee_id: number;
  loan_id?: number;
  profile_id?: number;
  first_name?: string;
  last_name?: string;
  relationship?: string;
  nominee_type?: string;
  date_of_birth?: string | null;
  gender?: string;
  mobile_no?: string;
  email?: string;
  aadhaar_number?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: any;
};

type Props = {
  profileId?: number | string | null;
  loanId?: number | string | null;
};

const LoanNominees: React.FC<Props> = ({ profileId = null, loanId = null }) => {
  const [data, setData] = useState<Nominee[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingNominee, setEditingNominee] = useState<Nominee | null>(null);

  // delete confirm
  const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Nominee | null>(null);
  const [deleting, setDeleting] = useState(false);
        const { user } = useAuth();
          const isAdmin = user?.role === "Admin";
          const isCustomer = user?.role === "Customer";

  // form fields for FormBuilder
  const formFields: FieldConfig[] = [
    { name: "first_name", label: "First Name", type: "text", placeholder: "Enter First Name", required: true },
    { name: "last_name", label: "Last Name", type: "text", placeholder: "Enter Last Name", required: true },
    { name: "relationship", label: "Relationship", type: "text", placeholder: "Enter Relationship" },
    // {
    //   name: "relationship",
    //   label: "Relationship",
    //   type: "chips",
    //   placeholder: "Select Relationship",
    //   required: true,
    //   options: ["Spouse", "Child", "Parent", "Sibling", "Other"],
    // },
    // {
    //   name: "nominee_type",
    //   label: "Nominee Type",
    //   type: "chips",
    //   placeholder: "Select Nominee Type",
    //   required: true,
    //   options: ["Primary", "Secondary", "Contingent"],
    // },
    { name: "date_of_birth", label: "Date of Birth", type: "date", placeholder: "Select Date of Birth", required: false },
    { name: "gender", label: "Gender", type: "chips", placeholder: "Select Gender", required: false, options: ["Male", "Female", "Other"] },
    { name: "mobile_no", label: "Mobile Number", type: "tel", placeholder: "Enter Mobile Number (10 digits)", required: true , minLength: 10, maxLength: 10 },
    { name: "email", label: "Email", type: "email", placeholder: "Enter Email Address", required: false },
    { name: "aadhaar_number", label: "Aadhaar Number", type: "text", placeholder: "Enter Aadhaar Number (12 digits)", required: false, minLength: 12, maxLength: 12 },
    { name: "address", label: "Address", type: "textarea", placeholder: "Enter Full Address", required: false },
    { name: "city", label: "City", type: "text", placeholder: "Enter City", required: false },
    { name: "state", label: "State", type: "text", placeholder: "Enter State", required: false },
    { name: "country", label: "Country", type: "text", placeholder: "Enter Country", required: false },
    { name: "pincode", label: "Pincode", type: "text", placeholder: "Enter Pincode (6 digits)", required: false, minLength: 6, maxLength: 6 },
    // { name: "status", label: "Status", type: "chips", placeholder: "Select Status", required: false, options: ["Active", "Inactive", "Pending"] },
  ];

  // helper: format date
  function formatDate(iso?: string | null) {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
    } catch {
      return iso;
    }
  }

  // load nominees (prefers profileId+loanId)
  const load = async () => {
    setLoading(true);
    setError(null);
    try { 
      if (profileId != null && loanId != null) {
        const res = await getloanNominee(profileId, loanId);
        // normalize response -> array
        let arr: Nominee[] = [];
        if (Array.isArray(res)) arr = res;
        else if (res && Array.isArray((res as any).data)) arr = (res as any).data;
        else if (res && typeof res === "object") arr = [res as Nominee];
        else arr = [];
        setData(arr);
      // } else {
      //   const res = await getloanNominees();
      //   const arr = Array.isArray(res) ? res : (res && Array.isArray((res as any).data) ? (res as any).data : []);
      //   setData(arr as Nominee[]);
      }
    } catch (err: any) {
      console.error("Failed to fetch nominees:", err);
      setError(err?.message ?? "Failed to load nominees");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // re-run when profileId/loanId change
  }, [profileId, loanId]);

  // open Add modal
  const handleAdd = () => {
    setEditingNominee(null);
    setModalOpen(true);
  };

  // open Edit modal with prefill
  const handleEdit = (item: Nominee) => {
    setEditingNominee(item);
    setModalOpen(true);
  };

  // open delete confirm
  const handleDelete = (item: Nominee) => {
    setSelectedForDelete(item);
    setConfirmDeleteModalOpen(true);
  };

  // submit handler for creating or updating
  const handleSubmit = async (values: Record<string, any>) => {
    // optionally attach loan_id/profile_id if available and not present in form
    if (profileId != null && !values.profile_id) values.profile_id = profileId;
    if (loanId != null && !values.loan_id) values.loan_id = loanId;

    try {
      if (editingNominee && editingNominee.nominee_id) {
        // update
        const id = editingNominee.nominee_id;
        const res = await updateNominee(id, values);
        if (!res.success) throw new Error(res.message ?? "Update failed");
        toast.success("Nominee updated");
      } else {
        // create
        const res = await createNominee(values);
        if (!res.success) throw new Error(res.message ?? "Create failed");
        toast.success("Nominee created");
      }
      setModalOpen(false);
      setEditingNominee(null);
      await load();
    } catch (err: any) {
      console.error("Nominee save error:", err);
      toast.error(err?.message ?? "Failed to save nominee");
    }
  };

  const handleCancelDelete = () => {
    setConfirmDeleteModalOpen(false);
    setSelectedForDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedForDelete) return;
    setDeleting(true);
    try {
      const id = selectedForDelete.nominee_id;
      const res = await deleteNominee(id);
      if (!res.success) throw new Error(res.message ?? "Delete failed");
      toast.success("Nominee deleted");
      await load();
    } catch (err: any) {
      console.error("Delete nominee error:", err);
      toast.error(err?.message ?? "Failed to delete nominee");
    } finally {
      setDeleting(false);
      setConfirmDeleteModalOpen(false);
      setSelectedForDelete(null);
    }
  };

  // small presentational component for label + value
  function LabelValue({ label, value }: { label: string; value?: React.ReactNode }) {
    return (
      <div className="flex flex-col sm:flex-row sm:items-start">
        <span className="font-medium text-gray-700 sm:w-32 md:w-36 lg:w-40 flex-shrink-0">{label}:</span>
        <span className="text-gray-800 flex-1 break-words whitespace-pre-wrap overflow-hidden break-all">{value ?? "—"}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-row justify-between items-center mb-5 gap-3">
        <h3 className="text-lg md:text-xl font-semibold text-gray-800">Nominees</h3>
        <div className="flex items-center gap-2">
          {/* <Button onClick={() => load()} variant="secondary">Refresh</Button> */}
         {!isCustomer &&( <Button className="flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-sm md:text-base" onClick={handleAdd}>
            <Icon icon="mdi:plus" width={18} />
            Add
          </Button>)}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-500 bg-gray-50 p-6 rounded-xl shadow-sm">Loading nominees…</div>
      )
      //  : error ? (
      //   <div className="text-center text-rose-600 py-6">{error}</div>
      // ) 
      : !data || data.length === 0  ? (
        <div className="text-center text-gray-500 bg-gray-50 p-6 rounded-xl shadow-sm">No nominees found. Please add one.</div>
      ) : (
        <div className="space-y-5">
          {data.map((item) => (
            <div key={item.nominee_id} className="bg-white p-5 rounded-xl shadow-md transition-all duration-300 hover:shadow-lg">
              <div className="flex flex-row items-center justify-between pb-3 mb-2 gap-3 border-b border-gray-200">
                <h4 className="font-semibold text-base md:text-lg text-gray-800">{item.first_name} {item.last_name}</h4>
                <div className="flex flex-wrap items-center gap-2">
                  {!isCustomer &&(<button onClick={() => handleEdit(item)} className="text-blue-600 border border-blue-400 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-50 text-sm md:text-base transition cursor-pointer" aria-label={`Edit nominee ${item.first_name}`}>
                    <Icon icon="mingcute:edit-line" className="text-md md:text-lg" />
                  </button>)}

                 {isAdmin &&( <button onClick={() => handleDelete(item)} className="text-rose-600 border border-rose-400 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-rose-50 text-sm md:text-base transition cursor-pointer" aria-label={`Delete nominee ${item.first_name}`}>
                    <Icon icon="fluent:delete-24-regular" className="text-md md:text-lg" />
                  </button>)}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 ">
                <div className="grid gap-x-6 gap-y-4 mt-4 lg:mt-0">
                  <LabelValue label="First Name" value={item.first_name} />
                  <LabelValue label="Last Name" value={item.last_name} />
                  <LabelValue label="Relationship" value={item.relationship} />
                  <LabelValue label="Nominee Type" value={item.nominee_type} />
                  <LabelValue label="Date Of Birth" value={formatDate(item.date_of_birth)} />
                  <LabelValue label="Gender" value={item.gender} />
                </div>

                <div className="grid gap-x-6 gap-y-4 mt-4 lg:mt-0">
                  <LabelValue label="Mobile No" value={item.mobile_no} />
                  <LabelValue label="Email" value={item.email} />
                  <LabelValue label="Aadhaar Number" value={item.aadhaar_number} />
                  <LabelValue label="Address" value={item.address} />
                  <LabelValue label="City / State" value={`${item.city ?? "—"}${item.state ? `, ${item.state}` : ""}`} />
                  <LabelValue label="Country / Pincode" value={`${item.country ?? "—"}${item.pincode ? ` · ${item.pincode}` : ""}`} />
                  <LabelValue label="Status" value={item.status} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={modalOpen} onClose={() => { setModalOpen(false); setEditingNominee(null); }} title={editingNominee ? "Edit Nominee" : "Add Nominee"}>
        {/* Pass initialValues to FormBuilder for edit; empty object for create */}
        <FormBuilder
          fields={formFields}
          defaultValues={editingNominee ?? {}}
          onSubmit={handleSubmit}
          onCancel={() => { setModalOpen(false); setEditingNominee(null); }}
        />
      </Modal>

      <Modal isOpen={confirmDeleteModalOpen} onClose={handleCancelDelete} title="Confirm delete">
        <div className="py-2">
          <p className="mb-4">
            Are you sure you want to delete <strong>{selectedForDelete ? `${selectedForDelete.first_name} ${selectedForDelete.last_name}` : "this record"}</strong>? This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3">
            <button className="px-4 py-2 rounded-lg border hover:bg-gray-100" disabled={deleting} onClick={handleCancelDelete}>
              Cancel
            </button>

            <button className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60" disabled={deleting} onClick={handleConfirmDelete}>
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LoanNominees;
